import store from '../../store/index'
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.vfs;

const GREY = '#bdbdbd';
const BLACK = '#000000';
const MUTED = '#666666';

async function handleGenerateAndShare() {
    const { blob, filename } = await generatePdf({
      dadosCompra: store.getters.dadosCompra,
      endereco: store.getters.endereco,
      empresa: store.getters.empresa,
      catalogo: store.getters.catalogo,
      carrinhoProdutos: store.getters.cartProducts,
      idPedido: store.getters.idPedido,
    });
    await sharePdfFile(blob, filename);
}

function formatCurrency(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '';
  return `R$ ${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function tableBlock(headers, rows, widths) {
  return {
    table: {
      headerRows: 1,
      widths: widths ?? headers.map(() => '*'),
      body: [
        headers.map((h) => ({
          text: h ?? '',
          fillColor: GREY,
          bold: true,
          alignment: 'left',
        })),
        ...rows.map((row) =>
          row.map((cell) => (typeof cell === 'object' && cell !== null ? cell : { text: cell ?? '', alignment: 'left' }))
        ),
      ],
    },
    margin: [0, 0, 0, 0],
  };
}

async function urlToBase64(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function getLogoBase64(catalogo) {
  if (catalogo?.img_url) {
    const b64 = await urlToBase64(catalogo.img_url);
    if (b64) return b64;
  }
  return null;
}

function getDate() {
  const date = new Date();

  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });


  const formattedParts = formatter.format(date);

  const finalResult = formattedParts.replace(',', '');

  return finalResult;
}

function formatEndereco(endereco) {
  if (!endereco) return '';
  const linha1 = [endereco.logradouro, endereco.numero].filter(Boolean).join(', ');
  const linha2 = [endereco.complemento, endereco.bairro].filter(Boolean).join(' - ');
  return [linha1, linha2].filter(Boolean).join(' - ');
}


function getExtraDescription(extra) {
  if (extra.quantidade > 0) {
    return `${extra.quantidade} un. ${extra.descricao} — ${formatCurrency(extra.preco)} un.`;
  }
  return extra.descricao;
}

function buildItemDescriptionStack(item) {
  const stack = [
    { text: item.descricao ?? '', bold: true },
  ];

  if (item.codigo) {
    stack.push({ text: `Cód.: ${item.codigo}`, fontSize: 8, color: MUTED });
  }

  (item.extras ?? []).forEach((extra) => {
    stack.push({
      text: getExtraDescription(extra),
      fontSize: 8,
      color: MUTED,
    });
  });


  const sku = item.skuSelecionado;
  if (sku) {
    if (sku.cor) stack.push({ text: `Cor: ${sku.cor}`, fontSize: 8, color: MUTED });
    if (sku.tamanho) stack.push({ text: `Tamanho: ${sku.tamanho}`, fontSize: 8, color: MUTED });
    if (sku.voltagem) stack.push({ text: `Voltagem: ${sku.voltagem}`, fontSize: 8, color: MUTED });
    if (sku.sabor) stack.push({ text: `Sabor: ${sku.sabor}`, fontSize: 8, color: MUTED });
  }

  return { stack, margin: [0, 2, 0, 2] };
}

function extractFields({ dadosCompra, endereco, empresa, catalogo, carrinhoProdutos, idPedido, currentUser }) {
  const itens = carrinhoProdutos ?? [];
  const totalItens = itens.reduce((sum, item) => sum + (parseFloat(item.valorTotal) || 0), 0);

  return {
    empresaNome: empresa?.nomefantasia,
    empresaCpfCgc: empresa?.cpfcnpj,
    vendedor: dadosCompra?.vendedor,
    dataPedido: getDate(),
    clienteNome: dadosCompra?.nome,
    clienteCpfCgc: dadosCompra?.cpf,
    clienteTelefone: dadosCompra?.telefone,
    enderecoTexto: formatEndereco(endereco),
    enderecoCidade: endereco?.cidade,
    enderecoUf: endereco?.uf,
    formaPagamento: dadosCompra?.formaPagamento,
    observacao: dadosCompra?.observacao,
    obsFeitaPeloCliente: dadosCompra?.obsFeitaPeloCliente,
    desconto: dadosCompra?.desconto,
    valorTotal: dadosCompra?.valorTotal ?? totalItens,
    totalComDesconto: dadosCompra?.totalComDesconto ?? totalItens - (parseFloat(dadosCompra?.desconto) || 0),
    quantidadeItens: itens.length,
    itens,
    idPedido,
    exibirObsFeitaPeloCliente: !!empresa?.exibirObsFeitaPeloCliente,
    camposPersonalizados: !!empresa?.camposPersonalizados,
    semPreco: currentUser?.trabalharSemPreco === true,
    semFinanceiro: currentUser?.trabalharSemFinanceiro === true,
    catalogo: catalogo
  };
}

async function buildDocDefinition(storeData) {
  const f = extractFields(storeData);
  const logoBase64 = await getLogoBase64(storeData.catalogo);

  const content = [];

  // Header
  content.push({
    columns: [
      logoBase64 ? { image: logoBase64, width: 60 } : { text: '', width: 0 },
      { text: 'Pedido Catálogo', alignment: 'center',  bold: true, fontSize: 16, margin: [0, 20, 0, 0] },
    ],
    margin: [0, 0, 0, 8],
  });
  content.push({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.5, lineColor: BLACK }],
    margin: [0, 0, 0, 16],
  });

  // Empresa
  content.push(tableBlock(['Empresa', 'CNPJ'], [[f.empresaNome, f.empresaCpfCgc]]));

  // Cliente
  content.push({ text: 'Dados do cliente:', bold: true, fontSize: 12, margin: [0, 4, 0, 4] });
  if(f.clienteCpfCgc) {
    content.push(tableBlock(['Cliente', 'CPF/CNPJ', 'Telefone'], [[f.clienteNome, f.clienteCpfCgc, f.clienteTelefone]]));
  } else {
    content.push(tableBlock(['Cliente', 'Telefone'], [[f.clienteNome, f.clienteTelefone]]));
  }
  
  if (f.enderecoTexto) {
    content.push(tableBlock(['Endereco', 'Cidade', 'UF'], [[f.enderecoTexto, f.enderecoCidade, f.enderecoUf]]));
  }

  // Pedido
  content.push({ text: 'Dados do pedido:', bold: true, fontSize: 12, margin: [0, 4, 0, 4] });

    content.push(tableBlock(['Data', 'Forma de pagamento'], [[f.dataPedido, f.formaPagamento]]));
    content.push(tableBlock(['Observação'], [[ f.observacao]]));



  // Itens
  content.push({ text: 'Itens do Pedido:', bold: true, fontSize: 12, margin: [0, 4, 0, 4] });
  if (f.semPreco) {
    content.push(
      tableBlock(
        ['Descrição', 'Qtd.'],
        f.itens.map((item) => [buildItemDescriptionStack(item), item.quantidade]),
        ['*', 'auto']
      )
    );
  } else {
    content.push(
      tableBlock(
        ['Descrição', 'Qtd.', 'Total'],
        f.itens.map((item) => [
          buildItemDescriptionStack(item),
          item.quantidade,
          formatCurrency(item.valorTotal),
        ]),
        ['*', 'auto', 'auto']
      )
    );
  }

  // Total
  content.push({ text: 'Total:', bold: true, fontSize: 12, margin: [0, 4, 0, 4] });
  content.push(
    tableBlock(
      ['Total Itens', 'Total Final'],
      [[f.quantidadeItens, formatCurrency(f.totalComDesconto)]]
    )
  );

  return {
    pageSize: 'A4',
    content,
    defaultStyle: { fontSize: 10 },
  };
}

export async function generatePdf(storeData) {
  const docDefinition = await buildDocDefinition(storeData);
  const filename = `${storeData?.idPedido ?? 'orcamento'}.pdf`;

  const blob = await new Promise((resolve) => {
    pdfMake.createPdf(docDefinition).getBlob((b) => resolve(b));
  });

  return { blob, filename };
}

export async function sharePdfFile(blob, filename = 'documento.pdf') {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function pdf() { handleGenerateAndShare() }